/**
 * primitives.module.css — the layout, hit-target and variant half of the design
 * primitives. `docs/design-direction.md` §6 (texture recipes) and §9.7–9.8 (chips,
 * sheet).
 *
 * WHAT IS **NOT** HERE, ON PURPOSE
 *
 * The printed marks themselves already exist as global recipes in
 * `apps/web/src/styles/texture.css` (§6: `.grain`, `.halftone`, `[data-ghost]`,
 * `.ruler`, `.stamp`, `.plate`, `.rule`, `.paper`, `.paper__tape`, `.silkscreen`,
 * `.faceplate`) and the type tiers in `styles/type.css` (`.t-display`, `.t-mono`,
 * `.t-11` …). The components attach those class names verbatim and this module adds
 * only what a *component* legitimately owns: box model, hit slop, positioning,
 * variants and the two component-level animations. Re-declaring a recipe here would
 * fork it — and the font-failure degradation in `fonts.css` hangs off the global type
 * class names, so text that carries a local class instead of `t-mono` silently opts
 * out of §4.4.
 *
 * FIVE RULES EVERY DECLARATION BELOW OBEYS
 *
 *  1. **Semantic aliases only** (§3.2). No `--ink-*`, `--paper-*`, `--stock-*`,
 *     `--warm-*` or `--cool` appears in this file; components speak `--bg-panel`,
 *     `--text`, `--border`, `--accent`, `--focus` … The raw palette is not an API.
 *  2. **Radii are `var(--r-cut)` (0) or `var(--r-round)` (999px)** — nothing between
 *     (§5.3).
 *  3. **Separation is a 1px printed rule, never a shadow** (§5.5). No `box-shadow`
 *     with a blur radius, no `backdrop-filter`, no glow. The only licensed blurred
 *     shadow in the app is `.miniplayer`, declared once in `tokens.css`.
 *  4. **Every length is a token.** Spacing comes from `--s1 --s2 --s3 --s4 --s6 --s8
 *     --drop --bar` (§5.1) and font sizes from the nine `--fs-*` (§4.3). Where the
 *     design contract names a value that is not on the spacing scale (§9.8's 48px
 *     header) it is written as an expression over the scale rather than as a literal,
 *     so the closed set stays closed.
 *  5. **Green does not exist** (§3.1, §12.1) — there is no hue in 70–170° in this
 *     file, and success/offline/state ink is `--focus` / `--success`.
 *
 * `prefers-reduced-motion` is a first-class state (§7.4) and is handled globally by
 * `motion.css`, which forces `animation-duration: 1ms` and one iteration on every
 * element. Both keyframes below are therefore written so that the element's **resting
 * style is the finished style** and the animation only plays *into* it: with motion
 * reduced the loader head sits lit on the first tick and the sheet's top rule is
 * simply drawn. Nothing here needs a `@media` block of its own.
 */

/* ────────────────────────────────────────────────────────────────────────────────
   Shared: hit targets (§5.6) and screen-reader-only copy.
   44px is simultaneously the rhythmic emphasis (`--drop`), the width of every
   section rule and the minimum touch target — that identity is the system's one
   joke and it costs nothing. `touch-action: manipulation` is inherited from the
   reset, so a tap never waits for a double-tap-zoom that cannot happen.
   ──────────────────────────────────────────────────────────────────────────────── */

/* Applied to every primitive the user can tap: the target itself is ≥44×44. */
.hit {
  min-inline-size: var(--drop);
  min-block-size: var(--drop);
}

/*
 * Hit slop for a control whose *visual* is deliberately smaller than its target
 * (§9.7's 32px chip in a 44px hit row). The expander is a pseudo-element, so the slop
 * is added inside the layout box: it never pushes a neighbour the way a margin would.
 */
.expand {
  position: relative;
}

.expand::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  inset-block-start: 50%;
  block-size: var(--drop);
  transform: translateY(-50%);
}

/*
 * Not `display: none` and not `visibility: hidden` — both remove the text from the
 * accessibility tree, and this exists precisely so a screen reader can read a state
 * that is otherwise carried by a printed mark.
 */
.sr {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* ────────────────────────────────────────────────────────────────────────────────
   Grain — §6.1 / §8.8. The layer, its data URI and its blend guard live in
   `texture.css`; the one state a component sets is the buffering lift: while audio is
   stalled mid-track the grain rises to .13 — the record dragging, which any selector
   reads instantly. The `:global(.grain)` prefix beats the `@supports` block that sets
   the resting .075 without resorting to `!important`.
   ──────────────────────────────────────────────────────────────────────────────── */

:global(.grain).buffering {
  opacity: 0.13;
}

/* ────────────────────────────────────────────────────────────────────────────────
   Ruler — the one-drop bar ruler, one motif with five jobs (§6.6).
   The ticks and their `:nth-child(3n)` emphasis live in `texture.css`; what belongs
   to the component is the wrapper (the loader needs a positioning context), the
   explicit downbeat class (so the emphasis is assertable and survives a DOM the
   `:nth-child` selector cannot see) and the advancing loader head.
   ──────────────────────────────────────────────────────────────────────────────── */

.ticks {
  position: relative;
  display: inline-flex;
  align-items: flex-end;
}

/*
 * THE ONE DROP, as a class. `texture.css` lights every third tick with
 * `.ruler i:nth-child(3n)`; this repeats the same two declarations on the element
 * itself so the emphasis is (a) visible to a test that has no CSS engine and (b)
 * independent of the tick's index if a variant ever renders a partial bar.
 */
.drop {
  block-size: 12px;
  background: var(--accent);
}

/*
 * The loader (job 3): a single `--accent` tick advancing one position per `--t8`,
 * driven by CSS animation only — there is no spinner anywhere in this app (§12.7)
 * and no JavaScript ticks a loading indicator.
 *
 * `--ruler-steps` is written by the component from its tick count; the pitch is one
 * 1px tick plus the 3px gap of §6.6's `.ruler`.
 */
.loader {
  --ruler-pitch: 4px;
}

/*
 * The loader's base ticks stay quiet: the ink is carried by the moving head, so the
 * static downbeats of `.ruler i:nth-child(3n)` are stepped back down. The compound
 * selector is what wins over the global rule (§6.6) without `!important`.
 */
.loader :global(.ruler) i:nth-child(3n) {
  block-size: 6px;
  background: var(--border-strong);
}

.head {
  position: absolute;
  inset-block-end: 0;
  inset-inline-start: 0;
  inline-size: 1px;
  block-size: 12px;
  background: var(--accent);
  animation: advance calc(var(--t8) * var(--ruler-steps, 12)) steps(var(--ruler-steps, 12), end)
    infinite;
}

@keyframes advance {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(calc(var(--ruler-steps, 12) * var(--ruler-pitch, 4px)));
  }
}

/* ────────────────────────────────────────────────────────────────────────────────
   Rule / DownbeatRule — §6.10. The downbeat rule (a full-width hairline with a 44px
   3px `--accent` bar flush left) is the ONLY heading decoration in the system, and
   its bar is `--drop` wide because that is the touch-target unit.
   ──────────────────────────────────────────────────────────────────────────────── */

/* `--drop` of air above a downbeat rule (§5.1); the heading sits under it. */
.section {
  margin-block-start: var(--drop);
}

.heading {
  margin-block-start: var(--s3);
  color: var(--text-2);
}

/*
 * The `emphasis` and `pending` tiers of §5.4, as they apply to a free-standing rule.
 * The `:global(.rule)` prefix is there only to beat the source-order coin-flip
 * between two single-class selectors — the recipe's ground colour and the tier's have
 * identical specificity otherwise.
 */
:global(.rule).strong {
  background: var(--border-strong);
}

/* A dashed hairline cannot be a 1px background, so the ground is dropped and the line
   becomes a border. The grain dropout mask of §6.10 still applies — a rule is exactly
   what that mask is for (§12.11). */
:global(.rule).dash {
  block-size: 0;
  background: none;
  border-block-start: 1px dashed var(--border-pending);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Stamp — §6.8. Rubber-stamped label print, ALWAYS cropped by a screen edge: half a
   stamp reads as confidence, a centred whole stamp reads as a sticker.
   `texture.css` owns the ink, the border, the grain dropout mask and the -7° tilt.
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * The tilt becomes a custom property so the closed set of angles the contract uses
 * (-7° print stamps, -4° `NO SIGNAL`, -2.5°/-4.5° alternating search stamps) is one
 * declaration instead of four rules. The `:global(.stamp)` prefix is only there to
 * beat the source-order coin-flip between two single-class selectors.
 */
:global(.stamp).stamp {
  display: inline-block;
  transform: rotate(var(--stamp-tilt, -7deg));
}

/*
 * `edge` crops the stamp against its clipping ancestor — the screen shell, which is
 * `overflow: hidden` and the reason a stamp can bleed at all. z-50 is the documented
 * layer for "stamps that crop the screen edge" (§5.7).
 */
.stamp[data-edge] {
  position: absolute;
  z-index: var(--z-stamp);
  pointer-events: none;
}

.stamp[data-edge='right'] {
  inset-inline-end: calc(var(--s8) * -1);
  inset-block-end: var(--bar);
}

.stamp[data-edge='left'] {
  inset-inline-start: calc(var(--s8) * -1);
  inset-block-end: var(--bar);
}

.stamp[data-edge='top'] {
  inset-inline-end: var(--s4);
  inset-block-start: calc(var(--s6) * -1);
}

/* A tappable stamp is the reduced-motion form of THE PULL UP (§7.3) and the "actions
   are words in square stamps" rule (§12.21): it keeps its own 44px target and it is
   not `pointer-events: none`. */
.stamp.press {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/*
 * The slam-in: `PULL UP!` arrives at scale 1.25 → 1 with no easing. §7.3 writes 60ms;
 * §7.1 closes the duration set, and `--t32` (83ms) is the listed value nearest to it,
 * so the closed set wins — at this scale the two are indistinguishable and a stray
 * duration is not worth the exception. Resting style is the finished style, so
 * reduced motion lands on `scale(1)`.
 */
.slam {
  animation: slam var(--t32) linear 1;
}

@keyframes slam {
  from {
    transform: rotate(var(--stamp-tilt, -7deg)) scale(1.25);
  }

  to {
    transform: rotate(var(--stamp-tilt, -7deg)) scale(1);
  }
}

/* ────────────────────────────────────────────────────────────────────────────────
   Cyrillic copy in a tracked mono tier — §4.3 / §12.16.
   Tracked uppercase Cyrillic reads as shouting, and `.t-11` / `.t-13` ship +0.16em /
   +0.10em. CSS cannot see the script of a string, so the components detect it with
   `hasCyrillic` from `@bl/shared` and add this class, which drops the tracking and
   stops forcing caps. Latin copy never gets it and is unchanged.
   ──────────────────────────────────────────────────────────────────────────────── */

:global(.t-mono).cyr {
  letter-spacing: 0.02em;
  text-transform: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   CropPlate — §6.9. `texture.css`'s `.plate` draws the two crop marks; the component
   adds the padding and the one 12px registration crosshair. NOTHING ELSE MARKS A
   CORNER — no shadows, no glows.
   ──────────────────────────────────────────────────────────────────────────────── */

.crop {
  padding: var(--s3);
}

.cross {
  position: absolute;
  inset-block-start: var(--s2);
  inset-inline-end: var(--s2);
  /*
   * The cool state ink. §3.2 exposes it only through its state aliases and forbids a
   * component from naming the raw `--cool`, so the registration mark — which the
   * palette lists under the same ink — is drawn in `--focus`.
   */
  stroke: var(--focus);
  stroke-width: 1;
  fill: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   Chips and filters — §9.7. Two kinds, both square-or-round by rule, never a
   pill-with-a-fill.
   ──────────────────────────────────────────────────────────────────────────────── */

/* Filter chip: rectangular, 1px `--border`, `--bg-raised`, 32px visual in a 44px hit
   row (the `.expand` pseudo-element carries the slop). */
.chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--s1);
  block-size: var(--s8);
  /* The visual is 32px tall, but it is never narrower than the one drop: a one-word
     chip must still be thumbable across, and `.expand` only widens the target
     vertically. */
  min-inline-size: var(--drop);
  padding-inline: var(--s3);
  border: 1px solid var(--border);
  border-radius: var(--r-cut);
  background: var(--bg-raised);
  color: var(--text-2);
  white-space: nowrap;
}

/*
 * Selected = inverted: the cream ground with knocked-out ink. `--text` *is* the paper
 * alias (§3.2 maps it to the same warm off-white), and there is deliberately no
 * `--bg-inverted` token, so inversion is spelled as ink-on-paper here. Inversion is
 * the strongest move in the system and is capped elsewhere (§5.5.6): a chip is far
 * under the 25%-of-viewport limit.
 */
.chip[data-selected='true'] {
  background: var(--text);
  border-color: var(--text);
  color: var(--text-on-accent);
}

.chip[disabled] {
  color: var(--text-3);
  background: var(--bg-panel);
}

.chip:active:not([disabled]) {
  background: var(--bg-pressed);
}

/* Tag chip: a stamped ring — 1px `--accent-quiet`, transparent, 44px tall. */
.tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-inline: var(--s4);
  border: 1px solid var(--accent-quiet);
  border-radius: var(--r-round);
  background: none;
  color: var(--accent-quiet);
  white-space: nowrap;
}

/* Active = solid ink with knocked-out type. */
.tag[data-active='true'] {
  background: var(--accent-quiet);
  color: var(--text-on-accent);
}

.tag[disabled] {
  border-color: var(--border);
  color: var(--text-3);
}

.tag:active:not([disabled]) {
  background: var(--accent-tint);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Sheet / modal — §9.8. `--bg-panel`, `--r-cut`, full-width, a 1px `--border-strong`
   top edge that draws in left→right, and a scrim that is `--scrim-bg` PLUS a halftone
   dot layer so the overlay reads printed rather than frosted. `backdrop-filter` is
   banned (§5.5).
   ──────────────────────────────────────────────────────────────────────────────── */

.layer {
  position: fixed;
  inset: 0;
  z-index: var(--z-sheet);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.scrim {
  position: absolute;
  inset: 0;
  background: var(--scrim-bg);
}

/*
 * `max-block-size` is 86% of `--app-h`, not §9.8's literal `86dvh`: height comes from
 * Telegram (`viewportStableHeight`), and a viewport unit inside Telegram's webview
 * measures the area *under* Telegram's own header — the sheet would overhang and its
 * footer would be unreachable (§10.1, §12.18).
 */
.sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  max-block-size: calc(var(--app-h) * 0.86);
  background: var(--bg-panel);
  border-radius: var(--r-cut);
  color: var(--text);
  transform: translateY(var(--sheet-drag, 0px));
  animation: rise var(--t8) var(--ease-cut) 1;
}

/*
 * The dialog is a focus *container*, not a control: it holds focus on open only so the
 * trap and screen readers have somewhere to land. The §5.4 ring stays on every real
 * control inside; painting it around the whole sheet would frame the panel itself.
 */
.sheet:focus,
.sheet:focus-visible {
  outline: none;
}

/* SHEET RISE (§7.2): a printed rule appearing, not a card lifting. */
@keyframes rise {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
}

/*
 * The top edge is its own element because it *draws* — `scaleX(0) → 1` from the left.
 * §7.2 writes 200ms, which is not on §7.1's closed list; `--t16` is the listed value
 * next to it and the closed list wins.
 */
.edge {
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  block-size: 1px;
  background: var(--border-strong);
  transform-origin: left center;
  animation: draw var(--t16) var(--ease-cut) 1;
}

@keyframes draw {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

/*
 * 48px header (§9.8), written over the spacing scale. `touch-action: none` because
 * drag-to-dismiss lives on the header ONLY — with the reset's `manipulation`,
 * Telegram's own sheet-dismiss gesture steals the drag (§7.3, §10.1).
 */
.header {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: var(--s3);
  min-block-size: calc(var(--drop) + var(--s1));
  padding-inline: var(--gutter);
  border-block-end: 1px solid var(--border);
  touch-action: none;
}

/* Three 12px chalk dashes (§8.4/§9.8) — flush left with the title, because nothing in
   this app is centred (§12.5) and a centred grabber is the platform cliché. */
.grabber {
  inline-size: var(--s3);
  block-size: var(--s3);
  background-image: repeating-linear-gradient(
    to bottom,
    var(--border-strong) 0 1px,
    transparent 1px 4px
  );
}

.title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--text);
}

.count {
  color: var(--text-3);
}

/* A word in a square stamp — never an icon-only × (§9.8, §12.21). */
.close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-inline: var(--s2);
  border: 1px solid var(--border);
  border-radius: var(--r-cut);
  color: var(--text);
  background: none;
}

.close:active {
  background: var(--bg-pressed);
}

/*
 * Keyboard focus inverts the stamp instead of hanging §5.4's blue rectangle off it.
 * The ring is still there for every other control; on this one the offset outline read
 * as a stray blue box in the header — and a filled stamp is louder than a ring anyway,
 * so nothing is lost for a keyboard user.
 */
.close:focus-visible {
  outline: none;
  background: var(--text);
  color: var(--bg-panel);
}

.body {
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: var(--s4) var(--gutter) calc(var(--s4) + env(safe-area-inset-bottom));
}

/* ────────────────────────────────────────────────────────────────────────────────
   SpecTable — §5.2's `8.5ch 1fr` spec grid: 11px mono caps label left, mono value
   right-aligned. A row whose value is missing is OMITTED, never em-dashed (§12.6).
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * `minmax(0, 1fr)` and not `1fr`: a `1fr` track's automatic minimum is min-content, so
 * one unbreakable value (`05.09.2026`, a long riddim name) sets a floor the table then
 * imposes on its whole column — which is how this screen last grew a horizontal scroll
 * (see wiki: nowplaying-layout-defects-2026-09-05 §3).
 *
 * `8.5ch` stays, and it is worth knowing WHY it is not the number it looks like: `ch`
 * resolves against THIS element's font, and this grid container inherits the 15px
 * `--ff-ui` body face — not the 11px mono its own `<dt>`s are set in. So `8.5ch` is
 * ~71px, not ~56px. That is fine at full width, where the column has 346px to spend,
 * and wrong inside a 136px plate, where it left 36px for a catalogue number needing 64.
 * The fix belongs THERE, not here: `nowplaying.module.css`'s `.catalogueSpec` narrows
 * the column for the one caller that is short of room. Setting a font on this rule to
 * make `ch` mean what it appears to mean would put a `font-size` in the primitive
 * layer, which §4.3 reserves for the nine-value scale and `primitives.test.tsx` fails
 * on by name.
 */
.spec {
  display: grid;
  grid-template-columns: 8.5ch minmax(0, 1fr);
  gap: var(--s1) var(--s3);
  align-items: baseline;
}

.label {
  color: var(--text-3);
}

.value {
  margin: 0;
  text-align: right;
  color: var(--text);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Paper — §6.11. Paper appears in exactly TWO places in the whole app: the label on
   the disc and the Queue set-list sheet. If it cannot rotate, tape, hard-cut and
   rule, it must be dark instead (§12.14) — so this class only adds the positioning
   context the tape needs and the ruling-locked padding.
   ──────────────────────────────────────────────────────────────────────────────── */

.paper {
  position: relative;
  padding: var(--s6) var(--s4);
}

/* The second strip: the recipe in `texture.css` places one at 22%; the pair sits at
   the top corners of the set-list sheet, leaning the other way. */
:global(.paper__tape).tape[data-strip='2'] {
  inset-inline-start: 66%;
  transform: rotate(3.5deg);
}
